<script lang="ts">
    // BLOCO — o cartão que agrupa conteúdo. É um componente só porque era um só o tempo todo: o
    // cartão do Zframe e o do Zgrid estavam escritos duas vezes, propriedade por propriedade, no
    // mesmo css_z.css; o ZsheetSection era um terceiro, com outro raio e outro fundo.
    //
    // O que muda de um uso para outro é a DENSIDADE do cabeçalho, não o cartão:
    //
    //   titulo  formulário de tela cheia — título grande, faixa cinza atrás      (era o Zframe)
    //   rotulo  pilha de cartões, no mobile e no desktop — rótulo miúdo          (era o ZsheetSection)
    //   barra   tabela — nome + ferramentas na mesma linha                       (é o Zgrid2)
    //
    // Quem usa quase sempre não chama este componente direto: usa Zframe ou ZsheetSection, que são
    // apelidos com a API de cada um. O Zblock existe para o desenho do cartão morar num lugar só.
    import Fa from 'svelte-fa'
    import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
    import { slide } from 'svelte/transition'
    import { createEventDispatcher } from 'svelte'
    import Zbutton from './zbutton.svelte'
    import type { GridButton } from './zgrid'

    const dispatch = createEventDispatcher()

    let className: string = ''
    export { className as class }

    export let densidade: 'titulo' | 'rotulo' | 'barra' = 'rotulo'
    /** texto principal do cabeçalho */
    export let title: string = ''
    /** texto de apoio à direita do título (densidades rotulo e barra) */
    export let meta: string = ''
    /** texto de apoio abaixo do título (densidade titulo) */
    export let headline: string = ''
    export let tone: 'default' | 'danger' | 'attention' = 'default'
    export let collapsible: boolean = false
    export let open: boolean = true
    /** sem espaçamento interno: para listas que desenham as próprias linhas até a borda */
    export let flush: boolean = false
    /** ocupa toda a altura disponível — é o que a tabela precisa, e mais ninguém */
    export let preenche: boolean = false
    /** botões do cabeçalho; para algo mais elaborado use o slot "acts" */
    export let acts: GridButton[] = []
    export let size: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 = null
    export let sizeM: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 = null
    /** estilo inline no cartão — usado por quem precisa passar variáveis CSS (ex.: a tabela) */
    export let style: string = null

    const isMobile = screen.width <= 900

    $: temCabecalho = !!(title || meta || headline || acts.length || $$slots.acts)

    function toggle() {
        if (!collapsible) return
        open = !open
        dispatch('toggle', open)
    }
</script>

<div
    class="zblock zblock-{densidade} zblock-tone-{tone} {className} z-col z-col_w-{size} z-col-m_w-{sizeM}"
    class:flush
    class:preenche
    {style}
>
    {#if temCabecalho}
        <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
        <div class="zblock_head" class:clickable={collapsible} on:click={toggle}>
            <div class="zblock_head_info">
                {#if title}
                    <div class="zblock_title">{title}</div>
                {/if}
                {#if headline}
                    <div class="zblock_headline">{headline}</div>
                {/if}
            </div>
            <div class="spacer"></div>
            {#if meta}
                <span class="zblock_meta">{meta}</span>
            {/if}
            <slot name="acts" />
            {#each acts as act}
                <Zbutton
                    class="ms-2 {act.class}"
                    icon={act.icon ? act.icon : null}
                    on:click={() => act.callback()}
                    label={!isMobile || act.mobileShowLabel ? act.label : ''}
                    color={act.color}
                />
            {/each}
            {#if collapsible}
                <span class="zblock_chevron" class:open><Fa icon={faChevronDown} /></span>
            {/if}
        </div>
    {/if}
    {#if open}
        <div class="zblock_body" transition:slide={{ duration: collapsible ? 160 : 0 }}>
            <slot />
        </div>
    {/if}
</div>

<style>
    /* ---- o cartão: um raio, um fundo, uma borda, para as três densidades ---------------------
       fundo translúcido (e não uma cor sólida) de propósito: funciona empilhado, dentro de popover
       e sobre qualquer fundo, nos dois temas, sem precisar de exceção no tema claro. */
    .zblock {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        max-width: 100%;
        border: 1px solid #99999920;
        border-radius: 10px;
        background-color: #99999910;
    }
    /* só a tabela estica; um bloco de formulário tem a altura do conteúdo */
    .zblock.preenche {
        height: 100%;
    }

    /* Bloco dentro de bloco não repete o cartão: cartão sobre cartão vira moldura dupla e afasta o
       conteúdo duas vezes. Acontece de verdade nos relatórios (o bloco do relatório carrega título,
       filtros e a tabela dentro) e no PDV. O de dentro fica só com o conteúdo — a separação já vem
       da linha do cabeçalho dele e do espaçamento do bloco de fora.
       Só vale para o tom neutro: um bloco de alerta aninhado precisa continuar colorido, que é a
       razão de ele existir. */
    .zblock :global(.zblock-tone-default) {
        border-color: transparent;
        background-color: transparent;
        border-radius: 0px;
    }

    .zblock-tone-danger {
        background-color: #e573731a;
        border-color: #e5737333;
    }
    .zblock-tone-attention {
        background-color: #e0a83a1a;
        border-color: #e0a83a33;
    }

    /* ---- cabeçalho ------------------------------------------------------------------------- */
    .zblock_head {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 0 0 auto;
    }
    .zblock_head.clickable {
        cursor: pointer;
    }
    .zblock_head_info {
        min-width: 0;
    }
    .zblock_headline {
        opacity: 0.6;
        line-height: 125%;
        padding-top: 4px;
    }
    .zblock_meta {
        font-size: 12px;
        opacity: 0.5;
        white-space: nowrap;
    }
    .zblock_chevron {
        font-size: 12px;
        opacity: 0.5;
        transition: transform 0.15s ease;
        display: inline-flex;
    }
    .zblock_chevron.open {
        transform: rotate(180deg);
    }

    .zblock_body {
        flex-grow: 1;
        min-width: 0;
    }

    /* ---- densidade: titulo (formulário de tela cheia) --------------------------------------- */
    .zblock-titulo > .zblock_head {
        padding: 16px;
        background: #99999920;
        border-bottom: 1px solid #99999913;
    }
    .zblock-titulo .zblock_title {
        font-size: 22px;
        font-weight: 300;
    }
    .zblock-titulo > .zblock_body {
        padding: 16px;
    }

    /* ---- densidade: rotulo (pilha de cartões) ------------------------------------------------ */
    .zblock-rotulo {
        padding: 12px;
    }
    .zblock-rotulo > .zblock_head {
        margin-bottom: 8px;
    }
    .zblock-rotulo > .zblock_head.clickable {
        margin-bottom: 0px;
    }
    .zblock-rotulo .zblock_title {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        opacity: 0.55;
    }
    /* o corpo abre/fecha com slide; a margem fica aqui (e não padding no cartão) para a animação
       não "pular" */
    .zblock-rotulo > .zblock_head.clickable + .zblock_body {
        margin-top: 8px;
    }
    /* listas que desenham as próprias linhas até a borda */
    .zblock-rotulo.flush {
        padding: 12px 0px;
    }
    .zblock-rotulo.flush > .zblock_head {
        padding: 0px 12px;
    }

    /* ---- densidade: barra (tabela) -----------------------------------------------------------
       Só a tabela recorta: ela precisa que os cantos arredondados cortem as linhas. Nas outras
       densidades o cartão NÃO pode recortar, senão engole o que passa da borda — o painel de um
       select, um tooltip, um menu. clip e não hidden: recorta igual, mas não vira contêiner de
       rolagem, e sem isso o cabeçalho fixo da tabela nunca grudaria no topo da tela. */
    .zblock-barra {
        overflow: clip;
    }
    .zblock-barra > .zblock_head {
        padding: 14px 16px;
        border-bottom: 1px solid #99999925;
        flex-wrap: wrap;
        gap: 12px;
    }
    .zblock-barra .zblock_title {
        font-size: 15px;
        font-weight: 600;
    }
    /* a tabela desenha o próprio espaçamento */
    .zblock-barra > .zblock_body {
        padding: 0px;
    }

    /* ---- tema claro -------------------------------------------------------------------------- */
    :global(.theme_light) .zblock {
        background-color: #ffffff;
        border-color: #88888833;
    }
    :global(.theme_light) .zblock-tone-danger {
        background-color: #fff6f6;
        border-color: #f0c9c9;
    }
    :global(.theme_light) .zblock-tone-attention {
        background-color: #fffaf0;
        border-color: #f0dfc0;
    }
    /* a densidade "barra" não leva faixa: o cabeçalho da tabela já se separa pela linha de baixo,
       e uma faixa a mais competiria com o cabeçalho das colunas logo abaixo */
    :global(.theme_light) .zblock-titulo > .zblock_head {
        background: #f3f4f5;
    }
</style>
